/**
 * PlansTab — Subscription plan CRUD + seed defaults.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { superAdminApi } from './superAdminApi';

const AVAILABLE_MODULES = [
  'admissions', 'sis', 'attendance', 'timetable', 'lms', 'exams', 'reports', 'library',
  'transport', 'hostel', 'access', 'communication', 'cms_website', 'alumni', 'finance',
  'hr_payroll', 'gamification', 'proctoring', 'scheduler', 'api_access',
];

const AUDIENCE_LABEL = {
  school: 'students',
  event: 'delegates',
  church: 'members',
  ministry: 'people',
  standalone: 'users',
};

const TIER_COLORS = {
  trial: 'bg-slate-500/20 text-slate-300 border-slate-400/30',
  basic: 'bg-sky-500/20 text-sky-300 border-sky-400/30',
  standard: 'bg-indigo-500/20 text-indigo-300 border-indigo-400/30',
  premium: 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-400/30',
  enterprise: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
};

const EMPTY_PLAN = {
  code: '', name: '', tier: 'basic', scope: 'school',
  price_monthly: 0, price_yearly: 0,
  currency: 'USD', support_level: 'community', branding_allowed: false, api_access: false,
  is_active: true, description: '', modules_enabled: [],
  max_students: null, max_staff: null, max_admins: null, max_storage_mb: null,
};

export const PlansTab = () => {
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['superadmin-plans', 'all'],
    queryFn: () => superAdminApi.listPlans(true),
  });

  const seedMut = useMutation({
    mutationFn: superAdminApi.seedPlans,
    onSuccess: (r) => {
      toast.success(`Seeded ${r.created} plans (total: ${r.total_plans})`);
      qc.invalidateQueries({ queryKey: ['superadmin-plans'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  const delMut = useMutation({
    mutationFn: (id) => superAdminApi.deletePlan(id),
    onSuccess: () => {
      toast.success('Plan deactivated');
      qc.invalidateQueries({ queryKey: ['superadmin-plans'] });
    },
  });

  const openNew = () => { setEditing({ ...EMPTY_PLAN }); setEditOpen(true); };
  const openEdit = (p) => { setEditing({ ...EMPTY_PLAN, ...p }); setEditOpen(true); };

  return (
    <div className="space-y-4" data-testid="superadmin-plans-tab">
      <div className="flex items-center gap-3">
        <h3 className="text-lg font-semibold text-white">Subscription Plans</h3>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => seedMut.mutate()}
                  className="text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-500/10 hover:bg-fuchsia-500/20" data-testid="plans-seed-btn">
            <Sparkles className="h-4 w-4 mr-1" /> Seed Defaults
          </Button>
          <Button size="sm" onClick={openNew}
                  className="bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="plans-create-btn">
            <Plus className="h-4 w-4 mr-1" /> New Plan
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading && <p className="text-slate-500">Loading...</p>}
        {plans.map(p => (
          <Card key={p.id} className="bg-white/5 backdrop-blur-xl border-white/10 text-white relative overflow-hidden">
            {!p.is_active && <div className="absolute inset-0 bg-slate-950/60 flex items-center justify-center">
              <Badge variant="outline" className="text-slate-400 border-slate-500/30">INACTIVE</Badge>
            </div>}
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <Badge className={`capitalize ${TIER_COLORS[p.tier] || TIER_COLORS.basic}`}>{p.tier}</Badge>
                  <CardTitle className="mt-2 text-xl">{p.name}</CardTitle>
                  <p className="text-xs text-slate-500 font-mono mt-1">{p.code}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">${p.price_monthly}</p>
                  <p className="text-xs text-slate-500">/month</p>
                  {p.price_yearly > 0 && <p className="text-xs text-slate-500 mt-1">${p.price_yearly}/yr</p>}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-slate-400">{p.description}</p>
              <div className="flex flex-wrap gap-1">
                {(p.modules_enabled || []).slice(0, 8).map(m => (
                  <Badge key={m} variant="outline" className="text-[10px] px-1.5 py-0 text-slate-300 border-white/10 capitalize">
                    {m.replace(/_/g, ' ')}
                  </Badge>
                ))}
                {(p.modules_enabled || []).length > 8 &&
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400 border-white/10">
                    +{p.modules_enabled.length - 8} more
                  </Badge>}
              </div>
              <div className="text-xs text-slate-500 grid grid-cols-2 gap-1 pt-2 border-t border-white/5">
                <span>Max {AUDIENCE_LABEL[p.scope || 'school'] || 'users'}: {p.max_students ?? '∞'}</span>
                <span>Max staff: {p.max_staff ?? '∞'}</span>
                {p.branding_allowed && <span className="col-span-2 text-fuchsia-400">✓ Custom branding</span>}
                {p.api_access && <span className="col-span-2 text-emerald-400">✓ API access</span>}
              </div>
              <div className="flex gap-2 pt-3">
                <Button size="sm" variant="outline" onClick={() => openEdit(p)}
                        className="text-white border-white/20 bg-white/5 hover:bg-white/10 flex-1"
                        data-testid={`plan-edit-${p.code}`}>
                  <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
                {p.is_active && (
                  <Button size="sm" variant="outline" onClick={() => {
                    if (confirm(`Deactivate plan "${p.name}"?`)) delMut.mutate(p.id);
                  }} className="text-rose-300 border-rose-400/30 bg-rose-500/10 hover:bg-rose-500/20">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {!isLoading && plans.length === 0 && (
          <p className="col-span-3 text-slate-500 text-sm">No plans defined. Click "Seed Defaults" to get started.</p>
        )}
      </div>

      <PlanEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        plan={editing}
        onSaved={() => qc.invalidateQueries({ queryKey: ['superadmin-plans'] })}
      />
    </div>
  );
};

const PlanEditDialog = ({ open, onOpenChange, plan, onSaved }) => {
  const [form, setForm] = useState(plan || EMPTY_PLAN);
  React.useEffect(() => { if (plan) setForm(plan); }, [plan]);

  const isEdit = !!form?.id;
  const mut = useMutation({
    mutationFn: (data) => isEdit ? superAdminApi.updatePlan(form.id, data) : superAdminApi.createPlan(data),
    onSuccess: () => {
      toast.success(isEdit ? 'Plan updated' : 'Plan created');
      onOpenChange(false);
      onSaved?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  const submit = () => {
    const payload = { ...form };
    delete payload.id;
    ['max_students', 'max_staff', 'max_admins', 'max_storage_mb'].forEach(k => {
      if (payload[k] === '' || payload[k] === null) payload[k] = null;
      else payload[k] = parseInt(payload[k]);
    });
    mut.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 text-white border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Plan' : 'New Plan'}</DialogTitle>
          <DialogDescription className="text-slate-400">Define tier, pricing, limits, and module access</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div><Label>Code *</Label>
            <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toLowerCase() })}
                   disabled={isEdit} className="bg-white/5 border-white/10 text-white" data-testid="plan-form-code" />
          </div>
          <div className="col-span-2"><Label>Name *</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                   className="bg-white/5 border-white/10 text-white" data-testid="plan-form-name" />
          </div>
          <div><Label>Tier</Label>
            <Select value={form.tier} onValueChange={v => setForm({ ...form, tier: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['trial', 'basic', 'standard', 'premium', 'enterprise', 'custom'].map(t =>
                  <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Scope</Label>
            <Select value={form.scope || 'school'} onValueChange={v => setForm({ ...form, scope: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="plan-form-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['school', 'event', 'church', 'ministry', 'standalone'].map(s =>
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Price / month</Label>
            <Input type="number" value={form.price_monthly}
                   onChange={e => setForm({ ...form, price_monthly: parseFloat(e.target.value) || 0 })}
                   className="bg-white/5 border-white/10 text-white" />
          </div>
          <div><Label>Price / year</Label>
            <Input type="number" value={form.price_yearly}
                   onChange={e => setForm({ ...form, price_yearly: parseFloat(e.target.value) || 0 })}
                   className="bg-white/5 border-white/10 text-white" />
          </div>
          <div><Label>Max {AUDIENCE_LABEL[form.scope || 'school'] || 'Users'}</Label>
            <Input type="number" value={form.max_students ?? ''}
                   onChange={e => setForm({ ...form, max_students: e.target.value === '' ? null : e.target.value })}
                   placeholder="∞" className="bg-white/5 border-white/10 text-white" />
          </div>
          <div><Label>Max Staff</Label>
            <Input type="number" value={form.max_staff ?? ''}
                   onChange={e => setForm({ ...form, max_staff: e.target.value === '' ? null : e.target.value })}
                   placeholder="∞" className="bg-white/5 border-white/10 text-white" />
          </div>
          <div><Label>Storage (MB)</Label>
            <Input type="number" value={form.max_storage_mb ?? ''}
                   onChange={e => setForm({ ...form, max_storage_mb: e.target.value === '' ? null : e.target.value })}
                   placeholder="∞" className="bg-white/5 border-white/10 text-white" />
          </div>
          <div className="col-span-3">
            <Label>Description</Label>
            <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                   className="bg-white/5 border-white/10 text-white" />
          </div>
          <div className="col-span-3 flex gap-6 pt-2">
            <label className="flex items-center gap-2">
              <Checkbox checked={form.branding_allowed}
                        onCheckedChange={v => setForm({ ...form, branding_allowed: !!v })}
                        data-testid="plan-form-branding" />
              <span className="text-sm">Allow custom branding</span>
            </label>
            <label className="flex items-center gap-2">
              <Checkbox checked={form.api_access}
                        onCheckedChange={v => setForm({ ...form, api_access: !!v })} />
              <span className="text-sm">API access</span>
            </label>
            <label className="flex items-center gap-2">
              <Checkbox checked={form.is_active}
                        onCheckedChange={v => setForm({ ...form, is_active: !!v })} />
              <span className="text-sm">Active</span>
            </label>
          </div>
          <div className="col-span-3">
            <Label>Modules Included ({(form.modules_enabled || []).length})</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 mt-2 p-3 rounded-lg bg-white/5 border border-white/10">
              {AVAILABLE_MODULES.map(m => (
                <label key={m} className="flex items-center gap-2 text-xs p-1 hover:bg-white/5 rounded">
                  <Checkbox checked={(form.modules_enabled || []).includes(m)}
                            onCheckedChange={v => {
                              const cur = form.modules_enabled || [];
                              setForm({ ...form, modules_enabled: v ? [...cur, m] : cur.filter(x => x !== m) });
                            }} />
                  <span className="capitalize">{m.replace(/_/g, ' ')}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  className="text-white border-white/20 bg-white/5">Cancel</Button>
          <Button onClick={submit} disabled={mut.isPending || !form.code || !form.name}
                  className="bg-indigo-500 hover:bg-indigo-400 text-white" data-testid="plan-form-save">
            {mut.isPending ? 'Saving...' : 'Save Plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
